import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Form, Input, Select } from '@crisellevaldez/speculo-ui';
import {
  DOMAIN_PROVIDERS,
  EmailDomainSetup,
  IDXSetupData,
  IDXSetupFormValues,
  cleanDomainInput,
  idxSetupSchema,
  isEmailDomainReady,
  resolveEmailDomain,
  toRootDomain,
} from '../schemas/onboarding.schema';
import { HttpsUrlInput } from '../../../common/HttpsUrlInput';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { SectionBox } from '../components/SectionBox';
import { ChoiceCard } from '../components/ChoiceCard';
import { QuestionLabel } from '../components/QuestionLabel';
import { SectionHeading } from '../components/SectionHeading';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { GoDaddyDelegateAccess } from '../components/GoDaddyDelegateAccess';
import { EmailDnsRecords } from '../components/EmailDnsRecords';
import { TwoFaContactFields } from '../components/TwoFaContactFields';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { useUser } from '../../../../hooks/useUser';

interface Stage4IDXSetupProps {
  onNext: (data: IDXSetupData) => void;
  initialData?: {
    idx_display_url?: string;
    email_domain?: Partial<EmailDomainSetup>;
  };
  isLoading?: boolean;
  /** Rendered at the top of the IDX section: the "how would you like to set up your IDX?" choice. */
  idxPathChoice?: React.ReactNode;
}

const EMPTY_EMAIL_DOMAIN: IDXSetupFormValues['email_domain'] = {
  domain: '',
  sendgrid_confirmed: false,
  godaddy_access_confirmed: false,
  provider_other_name: '',
  login_url: '',
  username: '',
  password: '',
  two_fa_contact_name: '',
  two_fa_contact_email: '',
  two_fa_contact_phone: '',
};

export const Stage4IDXSetup: React.FC<Stage4IDXSetupProps> = ({
  onNext,
  initialData,
  isLoading = false,
  idxPathChoice,
}) => {
  const { sendWebhook } = useOnboardingWebhook();
  const { user } = useUser();

  const saved = initialData?.email_domain;
  const form = useForm<IDXSetupFormValues, unknown, IDXSetupData>({
    resolver: zodResolver(idxSetupSchema),
    defaultValues: {
      idx_display_url: initialData?.idx_display_url ?? '',
      email_domain: {
        ...EMPTY_EMAIL_DOMAIN,
        ...saved,
        // "Use my IDX domain" starts unticked: the client types the email domain.
        domain_source: saved?.domain_source ?? 'different',
        // A "different" domain is typed; "same as IDX" is always derived below.
        domain: saved?.domain_source === 'different' ? (saved.domain ?? '') : '',
      },
    },
  });

  const errors = form.formState.errors.email_domain;
  const setupOption = form.watch('email_domain.setup_option');
  const domainSource = form.watch('email_domain.domain_source');
  const accessMethod = form.watch('email_domain.access_method');
  const provider = form.watch('email_domain.provider');
  // "Same as my IDX domain" follows the IDX Display URL as it's edited.
  const idxRootDomain = toRootDomain(form.watch('idx_display_url') ?? '');
  const emailDomain = resolveEmailDomain(
    domainSource,
    form.watch('idx_display_url') ?? '',
    form.watch('email_domain.domain')
  );

  // Each step shows once the one before it is answered.
  const domainReady = isEmailDomainReady(
    domainSource,
    form.watch('idx_display_url') ?? '',
    form.watch('email_domain.domain'),
    form.watch('email_domain.sendgrid_confirmed')
  );

  const setValue = <K extends Parameters<typeof form.setValue>[0]>(
    name: K,
    value: Parameters<typeof form.setValue<K>>[1]
  ) => form.setValue(name, value, { shouldValidate: form.formState.isSubmitted });

  // DNS records belong to one domain: a new domain or domain choice clears them
  // (and the SendGrid confirmation they were generated from).
  const clearDnsRecords = () => {
    setValue('email_domain.sendgrid_confirmed', false);
    setValue('email_domain.dns_records_domain', '');
  };

  const selectSetupOption = (option: 'self' | 'speculo') => {
    setValue('email_domain.setup_option', option);
    // "Same as my IDX domain" generates records from the SendGrid confirmation.
    if (
      option === 'self' &&
      domainSource === 'same_as_idx' &&
      form.getValues('email_domain.sendgrid_confirmed') === true
    ) {
      setValue('email_domain.dns_records_domain', idxRootDomain);
    }
  };

  const selectDomainSource = (source: 'same_as_idx' | 'different') => {
    if (source !== domainSource) clearDnsRecords();
    setValue('email_domain.domain_source', source);
  };

  const confirmSendgrid = (confirmed: boolean) => {
    setValue('email_domain.sendgrid_confirmed', confirmed);
    if (setupOption === 'self') {
      setValue('email_domain.dns_records_domain', confirmed ? idxRootDomain : '');
    }
  };

  const handleSubmit = (data: IDXSetupData) => {
    // Credentials go to Secret Manager on save, never to the webhook.
    const { password: _password, ...emailDomain } = data.email_domain;
    sendWebhook('Stage 4 - IDX Display URL', { ...data, email_domain: emailDomain }); // Fire and forget
    onNext(data);
  };

  return (
    <StageContainer
      title="IDX & Email"
      description="Set up the IDX site Remi uses to send properties to your leads by email and SMS, and the domain Remi sends email from."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8" noValidate>
        {/* IDX */}
        <div className="space-y-6">
          <SectionHeading title="IDX Setup" />
          {idxPathChoice}
          <FormSection>
            <Form.Control required>
              <QuestionLabel>What is your IDX Display URL?</QuestionLabel>
              <HttpsUrlInput
                {...form.register('idx_display_url', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (
                      domainSource === 'same_as_idx' &&
                      toRootDomain(e.target.value) !== idxRootDomain
                    ) {
                      clearDnsRecords();
                    }
                  },
                })}
                placeholder="search.yourcompany.com"
                error={form.formState.errors.idx_display_url?.message}
              />
              <TipBox>
                When Remi sends properties to your leads by email or SMS, it links them directly to
                your IDX site.
              </TipBox>
            </Form.Control>
          </FormSection>
        </div>

        {/* Remi Email Domain Setup */}
        <div className="space-y-6">
          <SectionHeading
            title="Email Setup"
            description="Remi sends emails from your own domain. Choose how you'd like to set it up."
          />

          <FormSection>
            <QuestionLabel>How would you like to set up your email domain?</QuestionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <ChoiceCard
                name="email-domain-setup-option"
                value="self"
                checked={setupOption === 'self'}
                onSelect={() => selectSetupOption('self')}
                title="I'll add the DNS records myself"
                description="We'll give you the DNS records to add at your domain provider."
              />
              <ChoiceCard
                name="email-domain-setup-option"
                value="speculo"
                checked={setupOption === 'speculo'}
                onSelect={() => selectSetupOption('speculo')}
                title="I'd like Speculo to set it up"
                description="Give Speculo access to your domain provider and we'll add the records for you."
              />
            </div>
            {errors?.setup_option?.message && (
              <p className="mt-2 text-sm text-destructive">{errors.setup_option.message}</p>
            )}
          </FormSection>

          {setupOption && (
            <FormSection>
              <Form.Control required>
                <QuestionLabel>Which domain should Remi send email from?</QuestionLabel>
                <div className="mt-3 space-y-3">
                  <Checkbox
                    id="email-domain-use-idx-domain"
                    checked={domainSource === 'same_as_idx'}
                    onChange={(e) =>
                      selectDomainSource(e.target.checked ? 'same_as_idx' : 'different')
                    }
                    label="Use my IDX domain"
                  />
                  {domainSource === 'same_as_idx' ? (
                    <HttpsUrlInput
                      value={idxRootDomain}
                      readOnly
                      placeholder="Enter your IDX Display URL above"
                    />
                  ) : (
                    <HttpsUrlInput
                      {...form.register('email_domain.domain', {
                        // Saved as the bare domain: scheme, www., trailing slash and path are dropped.
                        setValueAs: (value: string) => cleanDomainInput(value ?? ''),
                        onChange: () => setValue('email_domain.dns_records_domain', ''),
                      })}
                      placeholder="yourcompany.com"
                      error={errors?.domain?.message}
                    />
                  )}
                </div>
                {errors?.domain_source?.message && (
                  <p className="mt-2 text-sm text-destructive">{errors.domain_source.message}</p>
                )}
                <TipBox>
                  Remi sends emails to your leads from this domain. Untick &quot;Use my IDX
                  domain&quot; and enter another domain if your IDX domain is already connected to
                  SendGrid.
                </TipBox>
              </Form.Control>

              {domainSource === 'same_as_idx' && (
                <SectionBox variant="gray" type="child" className="mt-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="email-domain-sendgrid-confirmed"
                        checked={form.watch('email_domain.sendgrid_confirmed') === true}
                        onChange={(e) => confirmSendgrid(e.target.checked)}
                        label="I confirm this domain is not already connected to SendGrid."
                        disabled={!idxRootDomain}
                      />
                      <span className="text-red-600 dark:text-red-400 text-sm">*</span>
                    </div>
                    {!idxRootDomain && (
                      <p className="mt-1 text-sm text-gray-500">
                        Enter your IDX Display URL above first.
                      </p>
                    )}
                    {errors?.sendgrid_confirmed?.message && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.sendgrid_confirmed.message}
                      </p>
                    )}
                  </div>
                  {setupOption === 'self' &&
                    domainReady &&
                    form.watch('email_domain.dns_records_domain') === emailDomain && (
                      <EmailDnsRecords
                        domain={emailDomain}
                        generatedFor={emailDomain}
                        onGenerate={() => setValue('email_domain.dns_records_domain', emailDomain)}
                      />
                    )}
                </SectionBox>
              )}

              {domainSource === 'different' && setupOption === 'self' && domainReady && (
                <SectionBox variant="gray" type="child" className="mt-4">
                  <EmailDnsRecords
                    domain={emailDomain}
                    generatedFor={form.watch('email_domain.dns_records_domain')}
                    onGenerate={() => setValue('email_domain.dns_records_domain', emailDomain)}
                  />
                </SectionBox>
              )}
            </FormSection>
          )}

          {/* Option B: access for Speculo */}
          {setupOption === 'speculo' && domainReady && (
            <FormSection>
              <QuestionLabel>How would you like to give Speculo access?</QuestionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <ChoiceCard
                  name="domain-access-method"
                  value="godaddy_delegate"
                  checked={accessMethod === 'godaddy_delegate'}
                  onSelect={() => setValue('email_domain.access_method', 'godaddy_delegate')}
                  title="Share domain-only access (GoDaddy)"
                  description="Invite Speculo as a delegate. No password needed."
                />
                <ChoiceCard
                  name="domain-access-method"
                  value="credentials"
                  checked={accessMethod === 'credentials'}
                  onSelect={() => setValue('email_domain.access_method', 'credentials')}
                  title="Share my login credentials"
                  description="Share the login for your domain provider."
                />
              </div>
              {errors?.access_method?.message && (
                <p className="mt-2 text-sm text-destructive">{errors.access_method.message}</p>
              )}

              {accessMethod === 'godaddy_delegate' && (
                <SectionBox variant="gray" type="child" className="mt-4">
                  <GoDaddyDelegateAccess
                    checked={form.watch('email_domain.godaddy_access_confirmed') === true}
                    onCheckedChange={(checked) =>
                      setValue('email_domain.godaddy_access_confirmed', checked)
                    }
                    checkboxLabel="I've sent domain-only access to Speculo."
                    error={errors?.godaddy_access_confirmed?.message}
                  />
                </SectionBox>
              )}

              {accessMethod === 'credentials' && (
                <SectionBox variant="gray" type="child" className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Control required className="md:col-span-2">
                      <Form.Label>Domain provider</Form.Label>
                      <Controller
                        control={form.control}
                        name="email_domain.provider"
                        render={({ field }) => (
                          <Select
                            value={field.value ?? ''}
                            onChange={(value) =>
                              field.onChange(Array.isArray(value) ? value[0] : value)
                            }
                            options={DOMAIN_PROVIDERS.map((p) => ({
                              value: p.value,
                              label: p.label,
                            }))}
                            placeholder="Select your domain provider"
                            error={errors?.provider?.message}
                          />
                        )}
                      />
                    </Form.Control>

                    {provider === 'other' && (
                      <Form.Control required className="md:col-span-2">
                        <Form.Label>Domain provider name</Form.Label>
                        <Input
                          {...form.register('email_domain.provider_other_name')}
                          placeholder="e.g., Google Domains"
                          error={errors?.provider_other_name?.message}
                        />
                      </Form.Control>
                    )}

                    {provider && provider !== 'godaddy' && (
                      <Form.Control required className="md:col-span-2">
                        <Form.Label>Login URL</Form.Label>
                        <HttpsUrlInput
                          {...form.register('email_domain.login_url')}
                          placeholder="yourprovider.com/login"
                          error={errors?.login_url?.message}
                        />
                      </Form.Control>
                    )}

                    <Form.Control required>
                      <Form.Label>Username</Form.Label>
                      <Input
                        {...form.register('email_domain.username')}
                        placeholder="Username or email"
                        autoComplete="off"
                        error={errors?.username?.message}
                      />
                    </Form.Control>
                    <Form.Control required>
                      <Form.Label>Password</Form.Label>
                      <Input
                        {...form.register('email_domain.password')}
                        type="password"
                        placeholder="Enter password"
                        autoComplete="new-password"
                        error={errors?.password?.message}
                      />
                    </Form.Control>

                    <TwoFaContactFields
                      form={form}
                      fieldPrefix="email_domain."
                      user={user}
                      purpose="your domain provider account"
                    />
                  </div>
                </SectionBox>
              )}
            </FormSection>
          )}
        </div>

        <NextButton isLoading={isLoading} />
      </form>
    </StageContainer>
  );
};
